import {
    IonAvatar,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    chevronForwardOutline,
    americanFootballOutline,
    peopleOutline,
    refreshOutline,
    statsChartOutline,
    trophyOutline
} from "ionicons/icons";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Components from "./StatsFunction";
import {
    CompetitionDetail,
    CompetitionGame,
    PlayerDetail,
    PlayerSummary,
    PlayersById,
    Team,
    getCompetitionDetail,
    getGameDetail,
    getHomeData,
    getPlayerDetail,
    getPlayers
} from "../services/mexfbaApi";

import "./ResultStats.css";

interface RouteParams {
    id: string;
}

type StatCategory = "qb" | "rb" | "wr" | "def" | "ret" | "kp";

type LegacyStat = Record<string, string | number | undefined>;

interface StatisticsState {
    offense: LegacyStat[];
    defense: LegacyStat[];
    returns: LegacyStat[];
    kickers: LegacyStat[];
}

interface StatColumn {
    label: string;
    value: (stat: LegacyStat) => string | number;
}

interface PlayerStatRow {
    id: string;
    player: PlayerSummary;
    team: Team | undefined;
    image: string;
    values: Array<string | number>;
    primaryValue: number;
}


interface PlayerCompetitionContext {
    image: string;
    teamId: string;
}

const EMPTY_STATS: StatisticsState = {
    offense: [],
    defense: [],
    returns: [],
    kickers: []
};

function getNumber(value: unknown): number {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function getStatValue(
    stat: LegacyStat,
    property: string
): string | number {
    return stat[property] ?? 0;
}

function getPlayerName(player?: PlayerSummary): string {
    return String(player?.nombre || "Jugador sin nombre");
}

function getPlayerImage(player?: PlayerSummary): string {
    return String(player?.img || "");
}

function getPlayerTeamId(player?: PlayerSummary): string {
    return String(player?.equipo_id || "");
}
function getHistoryEntries(
    detail: PlayerDetail
): Array<{
    comp_id?: string;
    equipo_id?: string;
}> {
    const history = detail.Historial;

    if (!history) {
        return [];
    }

    return Array.isArray(history)
        ? history
        : Object.values(history);
}

function getCompetitionTeamId(
    detail: PlayerDetail,
    competitionId: string
): string {
    const history = getHistoryEntries(detail);

    if (history.length > 0) {
        const competitionEntry = history.find(function (entry) {
            return String(entry.comp_id || "") === competitionId;
        });

        return String(competitionEntry?.equipo_id || "");
    }

    return String(detail.Info.equipo_id || "");
}

function getPlayerInitial(player?: PlayerSummary): string {
    return getPlayerName(player).charAt(0).toUpperCase();
}

function getPlays(
    detail: Awaited<ReturnType<typeof getGameDetail>>
) {
    if (!detail.Jugadas) {
        return [];
    }

    return Array.isArray(detail.Jugadas)
        ? detail.Jugadas
        : Object.values(detail.Jugadas);
}

function getCompetitionGames(
    competition: CompetitionDetail
): CompetitionGame[] {
    const gamesById = new Map<string, CompetitionGame>();

    /*
        Sólo se consideran partidos de fase de grupos.
        Los encuentros de playoffs, semifinales, finales u otra fase
        quedan excluidos aunque existan dentro de Partidos o Final.
    */
    Object.values(competition.Partidos || {}).forEach(function (game) {
        if (
            game?.id &&
            String(game.partido || "").trim() === "grupos"
        ) {
            gamesById.set(String(game.id), game);
        }
    });

    return Array.from(gamesById.values());
}


function getQbr(stat: LegacyStat): string {
    return String(
        Components.getQBR(
            getNumber(getStatValue(stat, "att")),
            getNumber(getStatValue(stat, "comp")),
            getNumber(getStatValue(stat, "ydsPas")),
            getNumber(getStatValue(stat, "tdsPas")),
            getNumber(getStatValue(stat, "int"))
        )
    );
}

const ResultStats: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [competition, setCompetition] =
        useState<CompetitionDetail | null>(null);
    const [players, setPlayers] = useState<PlayersById>({});
    const [teams, setTeams] = useState<Team[]>([]);
    const [playerCompetitionContexts, setPlayerCompetitionContexts] =
    useState<Record<string, PlayerCompetitionContext>>({});
    const [stats, setStats] =
        useState<StatisticsState>(EMPTY_STATS);

    const [activeCategory, setActiveCategory] =
        useState<StatCategory>("qb");

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadStatistics = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [competitionData, homeData, playersData] =
                    await Promise.all([
                        getCompetitionDetail(id, forceRefresh),
                        getHomeData(forceRefresh),
                        getPlayers(forceRefresh)
                    ]);

                const offense: LegacyStat[] = [];
                const defense: LegacyStat[] = [];
                const returns: LegacyStat[] = [];
                const kickers: LegacyStat[] = [];

                const competitionGames =
                    getCompetitionGames(competitionData);

                await Promise.all(
                    competitionGames.map(async function (game) {
                        try {
                            const gameDetail = await getGameDetail(
                                game.id,
                                forceRefresh
                            );

                            getPlays(gameDetail).forEach(function (play) {
                                /*
                                    Se conserva la lógica histórica de
                                    StatsFunction para no alterar cálculos.
                                */
                                Components.getQB(play, offense);
                                Components.getRB(play, offense);
                                Components.getWR(play, offense);
                                Components.getDEF(play, defense);
                                Components.getSt(play, returns);
                                Components.getK(play, kickers);
                            });
                        } catch (gameError) {
                            /*
                                Un archivo de partido faltante no bloquea
                                el resto de las estadísticas.
                            */
                            console.warn(
                                `No fue posible abrir el partido ${game.id}.`,
                                gameError
                            );
                        }
                    })
                );

                offense.forEach(function (stat) {
                    stat.qbr = getQbr(stat);
                });
                

                setCompetition(competitionData);
                setTeams(homeData.teams);
                setPlayers(playersData);
                setPlayerCompetitionContexts({});

                setStats({
                    offense,
                    defense,
                    returns,
                    kickers
                });
            } catch (requestError) {
                console.error(
                    "No fue posible cargar las estadísticas:",
                    requestError
                );

                setError(
                    "No fue posible calcular las estadísticas de jugadores."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadStatistics();
        },
        [loadStatistics]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    const categoryColumns = useMemo<
        Record<StatCategory, StatColumn[]>
    >(
        function () {
            return {
                qb: [
                    {
                        label: "ATT",
                        value: function (stat) {
                            return getStatValue(stat, "att");
                        }
                    },
                    {
                        label: "CMP",
                        value: function (stat) {
                            return getStatValue(stat, "comp");
                        }
                    },
                    {
                        label: "YDS",
                        value: function (stat) {
                            return getStatValue(stat, "ydsPas");
                        }
                    },
                    {
                        label: "TD",
                        value: function (stat) {
                            return getStatValue(stat, "tdsPas");
                        }
                    },
                    {
                        label: "INT",
                        value: function (stat) {
                            return getStatValue(stat, "int");
                        }
                    },
                    {
                        label: "QBR",
                        value: function (stat) {
                            return getQbr(stat);
                        }
                    }
                ],

                rb: [
                    {
                        label: "ATT",
                        value: function (stat) {
                            return getStatValue(stat, "attA");
                        }
                    },
                    {
                        label: "YDS",
                        value: function (stat) {
                            return getStatValue(stat, "ydsAca");
                        }
                    },
                    {
                        label: "TD",
                        value: function (stat) {
                            return getStatValue(stat, "tdsAca");
                        }
                    },
                    {
                        label: "FUM",
                        value: function (stat) {
                            return getStatValue(stat, "fumble");
                        }
                    }
                ],

                wr: [
                    {
                        label: "TRG",
                        value: function (stat) {
                            return getStatValue(stat, "attR");
                        }
                    },
                    {
                        label: "REC",
                        value: function (stat) {
                            return getStatValue(stat, "compR");
                        }
                    },
                    {
                        label: "YDS",
                        value: function (stat) {
                            return getStatValue(stat, "ydsRec");
                        }
                    },
                    {
                        label: "TD",
                        value: function (stat) {
                            return getStatValue(stat, "tdsRec");
                        }
                    },
                    {
                        label: "FUM",
                        value: function (stat) {
                            return getStatValue(stat, "fumble");
                        }
                    }
                ],

                def: [
                    {
                        label: "TOT",
                        value: function (stat) {
                            return (
                                getNumber(getStatValue(stat, "tkl")) +
                                getNumber(getStatValue(stat, "assTkl"))
                            );
                        }
                    },
                    {
                        label: "SACK",
                        value: function (stat) {
                            return (
                                getNumber(getStatValue(stat, "sack")) +
                                getNumber(getStatValue(stat, "asSack")) / 2
                            ).toFixed(1);
                        }
                    },
                    {
                        label: "FF",
                        value: function (stat) {
                            return getStatValue(stat, "ff");
                        }
                    },
                    {
                        label: "FR",
                        value: function (stat) {
                            return getStatValue(stat, "fr");
                        }
                    },
                    {
                        label: "INT",
                        value: function (stat) {
                            return getStatValue(stat, "int");
                        }
                    },
                    {
                        label: "TFL",
                        value: function (stat) {
                            return getStatValue(stat, "tklL");
                        }
                    },
                    {
                        label: "PD",
                        value: function (stat) {
                            return getStatValue(stat, "pd");
                        }
                    }
                ],

                ret: [
                    {
                        label: "KR/PR",
                        value: function (stat) {
                            return `${getStatValue(
                                stat,
                                "attK"
                            )}/${getStatValue(stat, "attP")}`;
                        }
                    },
                    {
                        label: "YDK/YDP",
                        value: function (stat) {
                            return `${getStatValue(
                                stat,
                                "ydsK"
                            )}/${getStatValue(stat, "ydsP")}`;
                        }
                    },
                    {
                        label: "TDK/TDP",
                        value: function (stat) {
                            return `${getStatValue(
                                stat,
                                "tdsK"
                            )}/${getStatValue(stat, "tdsP")}`;
                        }
                    }
                ],

                kp: [
                    {
                        label: "XP",
                        value: function (stat) {
                            return `${getStatValue(
                                stat,
                                "attXP"
                            )}/${getStatValue(stat, "compXP")}`;
                        }
                    },
                    {
                        label: "FG",
                        value: function (stat) {
                            return `${getStatValue(
                                stat,
                                "attFG"
                            )}/${getStatValue(stat, "compFG")}`;
                        }
                    },
                    {
                        label: "P/AVG",
                        value: function (stat) {
                            const punts = getNumber(
                                getStatValue(stat, "punt")
                            );

                            const yards = getNumber(
                                getStatValue(stat, "ydsP")
                            );

                            return `${punts}/${Components.getAvg(
                                yards,
                                punts
                            )}`;
                        }
                    },
                    {
                        label: "LNG",
                        value: function (stat) {
                            return getStatValue(stat, "ydsFG");
                        }
                    }
                ]
            };
        },
        []
    );

    const activeStats = useMemo(function (): LegacyStat[] {
        if (activeCategory === "def") {
            return stats.defense;
        }

        if (activeCategory === "ret") {
            return stats.returns;
        }

        if (activeCategory === "kp") {
            return stats.kickers;
        }

        return stats.offense;
    }, [activeCategory, stats]);

    const playerRows = useMemo(function (): PlayerStatRow[] {

        const columns = categoryColumns[activeCategory];

        const filteredStats = activeStats.filter(function (stat) {
            if (activeCategory === "qb") {
                return getNumber(getStatValue(stat, "att")) > 0;
            }

            if (activeCategory === "rb") {
                return getNumber(getStatValue(stat, "attA")) > 0;
            }

            if (activeCategory === "wr") {
                return getNumber(getStatValue(stat, "attR")) > 0;
            }

            if (activeCategory === "def") {
                return (
                    getNumber(getStatValue(stat, "tkl")) +
                        getNumber(getStatValue(stat, "assTkl")) +
                        getNumber(getStatValue(stat, "sack")) +
                        getNumber(getStatValue(stat, "int")) >
                    0
                );
            }

            if (activeCategory === "ret") {
                return (
                    getNumber(getStatValue(stat, "attK")) +
                        getNumber(getStatValue(stat, "attP")) >
                    0
                );
            }

            return (
                getNumber(getStatValue(stat, "attXP")) +
                    getNumber(getStatValue(stat, "attFG")) +
                    getNumber(getStatValue(stat, "punt")) >
                0
            );
        });

        const getPrimaryValue: Record<
            StatCategory,
            (stat: LegacyStat) => number
        > = {
            qb: function (stat) {
                return getNumber(getStatValue(stat, "ydsPas"));
            },
            rb: function (stat) {
                return getNumber(getStatValue(stat, "ydsAca"));
            },
            wr: function (stat) {
                return getNumber(getStatValue(stat, "ydsRec"));
            },
            def: function (stat) {
                return (
                    getNumber(getStatValue(stat, "tkl")) +
                    getNumber(getStatValue(stat, "assTkl"))
                );
            },
            ret: function (stat) {
                return (
                    getNumber(getStatValue(stat, "ydsK")) +
                    getNumber(getStatValue(stat, "ydsP"))
                );
            },
            kp: function (stat) {
                return (
                    3 * getNumber(getStatValue(stat, "compFG")) +
                    getNumber(getStatValue(stat, "compXP"))
                );
            }
        };


        return filteredStats
            .map(function (stat) {
                const playerId = String(
                    getStatValue(stat, "id") || ""
                );

                const player = players[playerId];

                if (!player) {
                    return null;
                }

                const playerContext =
    playerCompetitionContexts[playerId];

const competitionTeamId = playerContext
    ? playerContext.teamId
    : getPlayerTeamId(player);


const team = teamsById.get(competitionTeamId);


                return {
    id: playerId,
    player,
    team,
    image:
        playerContext?.image ||
        getPlayerImage(player),
    values: columns.map(function (column) {
        return column.value(stat);
    }),
    primaryValue: getPrimaryValue[activeCategory](stat)
};


            })
            .filter(function (
                row
            ): row is PlayerStatRow {
                return row !== null;
            })
            .sort(function (firstRow, secondRow) {
                return secondRow.primaryValue - firstRow.primaryValue;
            })
            .slice(0, 25);
        }, [
        activeCategory,
        activeStats,
        categoryColumns,
        players,
        playerCompetitionContexts,
        teamsById
    ]);
const visiblePlayerIds = useMemo(function () {
    return playerRows.map(function (row) {
        return row.id;
    });
}, [playerRows]);

useEffect(
    function () {
        let cancelled = false;

        const playerIdsToLoad = visiblePlayerIds.filter(
            function (playerId) {
                return !Object.prototype.hasOwnProperty.call(
                    playerCompetitionContexts,
                    playerId
                );
            }
        );

        if (playerIdsToLoad.length === 0) {
            return;
        }

        async function loadVisiblePlayerContexts() {
            const contexts: Record<
                string,
                PlayerCompetitionContext
            > = {};

            const batchSize = 5;

            for (
                let index = 0;
                index < playerIdsToLoad.length;
                index += batchSize
            ) {
                const batch = playerIdsToLoad.slice(
                    index,
                    index + batchSize
                );

                const batchResults = await Promise.all(
                    batch.map(async function (playerId) {
                        try {
                            const playerDetail =
                                await getPlayerDetail(playerId);

                            return {
                                playerId,
                                context: {
                                    image: getPlayerImage(
                                        playerDetail.Info
                                    ),
                                    teamId: getCompetitionTeamId(
                                        playerDetail,
                                        id
                                    )
                                }
                            };
                        } catch (playerError) {
                            console.warn(
                                `No fue posible cargar el jugador ${playerId}.`,
                                playerError
                            );

                            return {
                                playerId,
                                context: {
                                    image: "",
                                    teamId: ""
                                }
                            };
                        }
                    })
                );

                batchResults.forEach(function (result) {
                    contexts[result.playerId] = result.context;
                });

                if (cancelled) {
                    return;
                }
            }

            if (!cancelled) {
                setPlayerCompetitionContexts(function (
                    currentContexts
                ) {
                    return {
                        ...currentContexts,
                        ...contexts
                    };
                });
            }
        }

        loadVisiblePlayerContexts();

        return function () {
            cancelled = true;
        };
    },
    [id, playerCompetitionContexts, visiblePlayerIds]
);



    const categoryTitle: Record<StatCategory, string> = {
        qb: "Líderes en pases",
        rb: "Líderes terrestres",
        wr: "Líderes receptores",
        def: "Líderes defensivos",
        ret: "Líderes en retornos",
        kp: "Líderes de equipos especiales"
    };

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadStatistics(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="result-stats-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !competition) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="result-stats-content">
                    <div className="result-stats-empty">
                        <IonIcon icon={peopleOutline} />
                        <strong>
                            No fue posible cargar las estadísticas
                        </strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = competition.Info;
    const columns = categoryColumns[activeCategory];
    const leader = playerRows[0];

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="result-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar a la competencia"
                            onClick={function () {
                                history.push(`/results/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugadores</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="result-stats-content"
            >
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Calculando estadísticas..."
                    />
                </IonRefresher>

                <section className="result-stats-hero">
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="result-stats-hero__placeholder">
                            <IonIcon icon={trophyOutline} />
                        </div>
                    )}

                    <div>
                        <span>ESTADÍSTICAS DE JUGADORES</span>
                        <h1>{info.nombre}</h1>
                        <p>
                            Líderes de la competencia por categoría.
                        </p>
                    </div>
                </section>

                <section className="result-stats-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/calendar/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/results/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton>Jugadores</IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeamResults/${id}`);
                        }}
                    >
                        Estadísticas
                    </IonButton>
                </section>

                <section className="result-stats-section">
                    <div className="result-stats-heading">
                        <div>
                            <span>RANKINGS</span>
                            <h2>{categoryTitle[activeCategory]}</h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <IonSegment
                        className="result-stats-filter"
                        value={activeCategory}
                        scrollable
                        onIonChange={function (event) {
                            setActiveCategory(
                                (event.detail.value ||
                                    "qb") as StatCategory
                            );
                        }}
                    >
                        <IonSegmentButton value="qb">
                            QB
                        </IonSegmentButton>

                        <IonSegmentButton value="rb">
                            RB
                        </IonSegmentButton>

                        <IonSegmentButton value="wr">
                            WR
                        </IonSegmentButton>

                        <IonSegmentButton value="def">
                            DEF
                        </IonSegmentButton>

                        <IonSegmentButton value="ret">
                            RET
                        </IonSegmentButton>

                        <IonSegmentButton value="kp">
                            K/P
                        </IonSegmentButton>
                    </IonSegment>

                    {leader && (
                        <button
                            className="result-stats-leader"
                            type="button"
                            onClick={function () {
                                history.push(`/player/${leader.id}`);
                            }}
                        >
                            <IonAvatar>
    {leader.image ? (
    <img
        src={leader.image}

            alt={`Foto de ${getPlayerName(
                leader.player
            )}`}
        />
    ) : leader.team?.logo ? (
        <img
            className="result-stats-team-logo"
            src={leader.team.logo}
            alt={`Logo de ${leader.team.nombre}`}
        />
    ) : (
        <span>
            {getPlayerInitial(leader.player)}
        </span>
    )}
</IonAvatar>


                            <div>
                                <span>LÍDER DE LA CATEGORÍA</span>
                                <strong>
                                    {getPlayerName(leader.player)}
                                </strong>
                                <small>
                                    {leader.team?.nombre ||
                                        "Equipo no disponible"}
                                </small>
                            </div>

                            <IonIcon icon={chevronForwardOutline} />
                        </button>
                    )}

                    {playerRows.length === 0 ? (
                        <div className="result-stats-no-results">
                            <IonIcon icon={americanFootballOutline} />
                            <strong>
                                No hay estadísticas disponibles
                            </strong>
                            <span>
                                Aún no se han registrado jugadas para esta
                                categoría.
                            </span>
                        </div>
                    ) : (
                        <div className="result-stats-table-wrapper">
                            <table className="result-stats-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>JUGADOR</th>

                                        {columns.map(function (column) {
                                            return (
                                                <th key={column.label}>
                                                    {column.label}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {playerRows.map(function (
                                        row,
                                        index
                                    ) {
                                        return (
                                            <tr key={row.id}>
                                                <td className="result-stats-table__rank">
                                                    {index + 1}
                                                </td>

                                                <td>
                                                    <button
                                                        className="result-stats-table__player"
                                                        type="button"
                                                        onClick={function () {
                                                            history.push(
                                                                `/player/${row.id}`
                                                            );
                                                        }}
                                                    >
                                                        <IonAvatar>
    {row.image ? (
    <img
        src={row.image}

            alt=""
        />
    ) : row.team?.logo ? (
        <img
            className="result-stats-team-logo"
            src={row.team.logo}
            alt={`Logo de ${row.team.nombre}`}
        />
    ) : (
        <span>
            {getPlayerInitial(row.player)}
        </span>
    )}
</IonAvatar>


                                                        <span>
                                                            <strong>
                                                                {getPlayerName(
                                                                    row.player
                                                                )}
                                                            </strong>

                                                            <small>
                                                                {row.team
                                                                    ?.nombre ||
                                                                    "—"}
                                                            </small>
                                                        </span>
                                                    </button>
                                                </td>

                                                {row.values.map(
                                                    function (
                                                        value,
                                                        valueIndex
                                                    ) {
                                                        return (
                                                            <td
                                                                key={`${row.id}-${valueIndex}`}
                                                            >
                                                                {value}
                                                            </td>
                                                        );
                                                    }
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default ResultStats;
